import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { useImperativeHandle } from 'react';
import { useControl } from 'react-map-gl/maplibre';

// Inlined from @mapbox/mapbox-gl-draw/src/lib/theme.js (not exported in package.json exports).
// The gl-draw-lines entry is patched for MapLibre GL JS compatibility: mapbox-gl-draw 1.5.x uses
// bare array literals as branch values in 'line-dasharray' expressions, but MapLibre requires
// them wrapped with ['literal', ...].
const blue = '#3bb2d0';
const orange = '#fbb03b';
const white = '#fff';

const maplibreCompatibleTheme = [
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': ['case', ['==', ['get', 'active'], 'true'], orange, blue],
      'fill-opacity': 0.1,
    },
  },
  {
    id: 'gl-draw-lines',
    type: 'line',
    filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': ['case', ['==', ['get', 'active'], 'true'], orange, blue],
      'line-dasharray': [
        'case',
        ['==', ['get', 'active'], 'true'],
        ['literal', [0.2, 2]],
        ['literal', [2, 0]],
      ],
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-point-outer',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 7, 5],
      'circle-color': white,
    },
  },
  {
    id: 'gl-draw-point-inner',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 5, 3],
      'circle-color': ['case', ['==', ['get', 'active'], 'true'], orange, blue],
    },
  },
  {
    id: 'gl-draw-vertex-outer',
    type: 'circle',
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['==', 'meta', 'vertex'],
      ['!=', 'mode', 'simple_select'],
    ],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 7, 5],
      'circle-color': white,
    },
  },
  {
    id: 'gl-draw-vertex-inner',
    type: 'circle',
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['==', 'meta', 'vertex'],
      ['!=', 'mode', 'simple_select'],
    ],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 5, 3],
      'circle-color': orange,
    },
  },
  {
    id: 'gl-draw-midpoint',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'midpoint']],
    paint: { 'circle-radius': 3, 'circle-color': orange },
  },
];

export function DrawControl({ ref, ...props }) {
  const drawRef = useControl(
    () => new MapboxDraw({ styles: maplibreCompatibleTheme, ...props }),
    ({ map }) => {
      if (props.onCreate) {
        map.on('draw.create', props.onCreate);
      }
      if (props.onUpdate) {
        map.on('draw.update', props.onUpdate);
      }
      if (props.onDelete) {
        map.on('draw.delete', props.onDelete);
      }
      if (props.onModeChange) {
        map.on('draw.modechange', props.onModeChange);
      }
    },
    ({ map }) => {
      if (props.onCreate) {
        map.off('draw.create', props.onCreate);
      }
      if (props.onUpdate) {
        map.off('draw.update', props.onUpdate);
      }
      if (props.onDelete) {
        map.off('draw.delete', props.onDelete);
      }
      if (props.onModeChange) {
        map.off('draw.modechange', props.onModeChange);
      }
    },
    { position: props.position },
  );

  useImperativeHandle(ref, () => drawRef, [drawRef]);

  return null;
}
